import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/api';

interface GlobalState {
  // AI 분석 모드
  analysisMode: 'single' | 'consensus';
  
  // 설정값들
  settings: {
    newsUpdateInterval: number;
    enableRssNews: boolean;
    enableApiNews: boolean;
    aiAnalysisInterval: number;
    telegramNotifications: boolean;
    language: 'ko' | 'en';
  } | null;
  
  // 뉴스 데이터
  news: any[];
  newsLastUpdated: Date | null;
  
  // AI 예측 데이터
  predictions: any[];
  predictionStats: any | null;
  
  // 거래 상태
  tradingStats: any | null;
  positionStatus: any | null;
  
  // 실시간 데이터
  currentPrice: number | null;
  marketData: any | null;
  
  // 로딩 상태
  isLoading: boolean;
  
  // 에러 상태
  error: string | null;
}

interface GlobalStateContextType {
  state: GlobalState;
  
  // 분석 모드 관련
  setAnalysisMode: (mode: 'single' | 'consensus') => void;
  
  // 설정 관련
  updateSettings: (settings: Partial<GlobalState['settings']>) => void;
  loadSettings: () => Promise<void>;
  
  // 뉴스 관련
  loadNews: (refresh?: boolean) => Promise<void>;
  
  // 예측 관련
  loadPredictions: () => Promise<void>;
  
  // 전체 데이터 새로고침
  refreshAllData: () => Promise<void>;
  
  // 실시간 데이터 구독
  subscribeToRealTimeUpdates: () => void;
  unsubscribeFromRealTimeUpdates: () => void;
}

const GlobalStateContext = createContext<GlobalStateContextType | undefined>(undefined);

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};

interface GlobalStateProviderProps {
  children: React.ReactNode;
}

export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({ children }) => {
  const [state, setState] = useState<GlobalState>({
    analysisMode: 'single', // 기본값은 single이지만 서버에서 즉시 덮어씀
    settings: null,
    news: [],
    newsLastUpdated: null,
    predictions: [],
    predictionStats: null,
    tradingStats: null,
    positionStatus: null,
    currentPrice: null,
    marketData: null,
    isLoading: true,
    error: null,
  });

  // API 기본 URL
  const getApiUrlLocal = useCallback(() => {
    return getApiUrl();
  }, []);

  // 분석 모드 설정
  const setAnalysisMode = useCallback(async (mode: 'single' | 'consensus') => {
    console.log('🔄 분석 모드 변경 시도:', mode);
    
    // 즉시 UI 업데이트 (사용자 경험 개선)
    setState(prev => ({ ...prev, analysisMode: mode }));
    
    // 서버에 저장 시도 (consensusEnabled로 매핑)
    try {
      const consensusEnabled = mode === 'consensus';
      const response = await fetch(`${getApiUrlLocal()}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          consensusEnabled,
          enableConsensusSimulation: consensusEnabled,
          analysisMode: mode 
        }),
      });
      
      if (response.ok) {
        console.log('✅ 서버에 분석 모드 저장 성공:', mode, '(consensusEnabled:', consensusEnabled, ')');
        // localStorage에도 백업 저장 (시크릿 모드에서는 무시될 수 있음)
        try {
          localStorage.setItem('analysisMode', mode);
        } catch (e) {
          console.warn('localStorage 저장 실패 (시크릿 모드일 수 있음):', e);
        }
      } else {
        console.error('❌ 서버 분석 모드 저장 실패:', response.status);
        // 서버 저장 실패 시 상태는 이미 업데이트됨
      }
    } catch (error) {
      console.error('❌ 분석 모드 저장 오류:', error);
      // 네트워크 오류 시에도 UI는 이미 업데이트됨
    }
  }, [getApiUrlLocal]);

  // 초기 설정 로드 (서버 우선 - 강화된 버전)
  const loadInitialSettings = useCallback(async () => {
    console.log('📥 서버에서 초기 설정 로드 시작...');
    
    try {
      const response = await fetch(`${getApiUrlLocal()}/api/settings`);
      
      if (response.ok) {
        const settings = await response.json();
        console.log('✅ 서버에서 설정 로드 성공');
        console.log('🔍 서버 consensusEnabled:', settings.consensusEnabled);
        console.log('🔍 서버 enableConsensusSimulation:', settings.enableConsensusSimulation);
        
        // 서버의 consensusEnabled 설정을 강제로 analysisMode에 반영
        const analysisMode: 'single' | 'consensus' = settings.consensusEnabled === true ? 'consensus' : 'single';
        
        console.log('🎯 서버 기반 최종 분석 모드:', analysisMode);
        console.log('🎯 consensusEnabled === true?', settings.consensusEnabled === true);
        console.log('🎯 typeof consensusEnabled:', typeof settings.consensusEnabled);
        
        // 상태 업데이트 - 서버 설정을 강제로 적용
        setState(prev => ({ 
          ...prev, 
          analysisMode, // 서버 설정 강제 적용
          settings: {
            newsUpdateInterval: settings.newsUpdateInterval || 15,
            enableRssNews: settings.enableRssNews !== false,
            enableApiNews: settings.enableApiNews !== false,
            aiAnalysisInterval: settings.aiAnalysisInterval || 15,
            telegramNotifications: settings.telegramNotifications !== false,
            language: settings.language || 'ko',
          },
          isLoading: false 
        }));
        
        console.log('🔄 상태 업데이트 완료 - analysisMode:', analysisMode);
        
        // localStorage에 서버 설정 동기화 (시크릿 모드에서는 무시될 수 있음)
        try {
          localStorage.setItem('analysisMode', analysisMode);
          console.log('💾 localStorage에 동기화:', analysisMode);
        } catch (e) {
          console.warn('⚠️ localStorage 저장 실패 (시크릿 모드일 수 있음):', e);
        }
        
        // 강제로 한 번 더 상태 업데이트 (혹시 모를 타이밍 이슈 방지)
        setTimeout(() => {
          setState(prev => ({ ...prev, analysisMode }));
          console.log('🔄 강제 재설정 완료:', analysisMode);
          
          // 최종 확인
          setTimeout(() => {
            console.log('🔍 최종 확인 - 현재 analysisMode 상태:', analysisMode);
            if (analysisMode === 'consensus') {
              console.log('✅ 컨센서스 모드가 정상적으로 설정되었습니다!');
            } else {
              console.log('❌ 여전히 단일 모드입니다. 추가 디버깅이 필요합니다.');
            }
          }, 1000);
        }, 500);
        
        return; // 성공 시 여기서 종료
        
      } else {
        console.error('❌ 서버 설정 로드 실패:', response.status);
      }
    } catch (error) {
      console.error('❌ 초기 설정 로드 오류:', error);
    }
    
    // 서버 연결 실패 시에만 localStorage 사용 (폴백)
    console.log('🔄 서버 연결 실패, localStorage 폴백 시도...');
    try {
      const localMode = localStorage.getItem('analysisMode') as 'single' | 'consensus';
      if (localMode && (localMode === 'single' || localMode === 'consensus')) {
        console.log('📱 localStorage에서 복원:', localMode);
        setState(prev => ({ ...prev, analysisMode: localMode, isLoading: false }));
      } else {
        console.log('🔄 기본값 사용: single 모드');
        setState(prev => ({ ...prev, analysisMode: 'single', isLoading: false }));
      }
    } catch (e) {
      console.warn('⚠️ localStorage 접근 실패:', e);
      console.log('🔄 기본값 사용: single 모드');
      setState(prev => ({ ...prev, analysisMode: 'single', isLoading: false }));
    }
  }, [getApiUrlLocal]);

  // 설정 로드 (기존 설정 업데이트용)
  const loadSettings = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrlLocal()}/api/settings`);
      if (response.ok) {
        const settings = await response.json();
        
        // consensusEnabled를 analysisMode로 변환
        const analysisMode = settings.consensusEnabled ? 'consensus' : 'single';
        
        setState(prev => ({
          ...prev,
          settings: {
            newsUpdateInterval: settings.newsUpdateInterval || 15,
            enableRssNews: settings.enableRssNews !== false,
            enableApiNews: settings.enableApiNews !== false,
            aiAnalysisInterval: settings.aiAnalysisInterval || 15,
            telegramNotifications: settings.telegramNotifications !== false,
            language: settings.language || 'ko',
          },
          analysisMode,
        }));
        
        // localStorage도 업데이트
        localStorage.setItem('analysisMode', analysisMode);
        console.log('🔄 설정 업데이트:', analysisMode);
        
      } else {
        console.warn('설정 로드 실패:', response.status);
      }
    } catch (error) {
      console.error('설정 로드 실패:', error);
    }
  }, [getApiUrlLocal]);

  // 설정 업데이트
  const updateSettings = useCallback(async (newSettings: Partial<GlobalState['settings']>) => {
    setState(prev => ({
      ...prev,
      settings: prev.settings ? { ...prev.settings, ...newSettings } : null,
    }));
    
    try {
      await fetch(`${getApiUrlLocal()}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings),
      });
    } catch (error) {
      console.error('설정 업데이트 실패:', error);
    }
  }, [getApiUrlLocal]);

  // 뉴스 로드
  const loadNews = useCallback(async (refresh = false) => {
    try {
      const language = state.settings?.language || 'ko';
      const languageParam = language === 'ko' ? 'korean' : 'english';
      const refreshParam = refresh ? '&refresh=true' : '';
      
      const response = await fetch(`${getApiUrlLocal()}/api/news?language=${languageParam}&page=1&limit=20${refreshParam}`);
      if (response.ok) {
        const data = await response.json();
        
        setState(prev => ({
          ...prev,
          news: data.articles || [],
          newsLastUpdated: new Date(),
        }));
      } else {
        // API 실패 시 빈 배열로 설정
        setState(prev => ({
          ...prev,
          news: [],
          newsLastUpdated: new Date(),
        }));
      }
    } catch (error) {
      console.error('뉴스 로드 실패:', error);
      // 에러 시에도 빈 배열로 설정하고 계속 진행
      setState(prev => ({
        ...prev,
        news: [],
        newsLastUpdated: new Date(),
      }));
    }
  }, [getApiUrlLocal, state.settings?.language]);

  // 예측 데이터 로드
  const loadPredictions = useCallback(async () => {
    try {
      const [statsResponse, predictionsResponse] = await Promise.all([
        fetch(`${getApiUrlLocal()}/api/predictions/performance`),
        fetch(`${getApiUrlLocal()}/api/predictions/all`),
      ]);
      
      let stats: any = null;
      let predictions: any[] = [];
      
      if (statsResponse.ok) {
        stats = await statsResponse.json();
      }
      
      if (predictionsResponse.ok) {
        const predictionsData = await predictionsResponse.json();
        predictions = Array.isArray(predictionsData) ? predictionsData : predictionsData.predictions || [];
      }
      
      setState(prev => ({
        ...prev,
        predictionStats: stats,
        predictions,
      }));
    } catch (error) {
      console.error('예측 데이터 로드 실패:', error);
      // 에러 시에도 기본값으로 설정하고 계속 진행
      setState(prev => ({
        ...prev,
        predictionStats: null,
        predictions: [],
      }));
    }
  }, [getApiUrlLocal]);

  // 전체 데이터 새로고침
  const refreshAllData = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // 뉴스와 예측 데이터만 로드 (설정은 loadInitialSettings에서 처리)
      const promises = [
        loadNews().catch(err => console.warn('News load failed:', err)),
        loadPredictions().catch(err => console.warn('Predictions load failed:', err)),
      ];
      
      // 최대 5초 후에는 로딩 완료 처리
      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 5000));
      
      await Promise.race([
        Promise.allSettled(promises),
        timeoutPromise
      ]);
      
    } catch (error) {
      console.error('데이터 새로고침 중 오류:', error);
    } finally {
      // 항상 로딩 상태 해제
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [loadNews, loadPredictions]);

  // 실시간 데이터 구독
  const subscribeToRealTimeUpdates = useCallback(() => {
    // WebSocket 또는 Socket.IO 연결
    console.log('실시간 업데이트 구독 시작');
  }, []);

  const unsubscribeFromRealTimeUpdates = useCallback(() => {
    console.log('실시간 업데이트 구독 해제');
  }, []);

  // 초기 데이터 로드 - localStorage 초기화 제거
  useEffect(() => {
    // 서버에서 초기 설정 로드 (localStorage 초기화 제거)
    loadInitialSettings();
  }, [loadInitialSettings]);

  // 정기적 데이터 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      loadNews();
      loadPredictions();
    }, 30000); // 30초마다
    
    return () => clearInterval(interval);
  }, [loadNews, loadPredictions]);

  const contextValue: GlobalStateContextType = {
    state,
    setAnalysisMode,
    updateSettings,
    loadSettings,
    loadNews,
    loadPredictions,
    refreshAllData,
    subscribeToRealTimeUpdates,
    unsubscribeFromRealTimeUpdates,
  };

  return (
    <GlobalStateContext.Provider value={contextValue}>
      {children}
    </GlobalStateContext.Provider>
  );
}; 